import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { ArrowLeft, Info, Trash2 } from "lucide-react";

import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { CampoDeRango } from "@/components/referencias/CampoDeRango";
import { FichaDeComparable } from "@/components/referencias/FichaDeComparable";
import { SelectorMultiple } from "@/components/SelectorMultiple";
import { PanelDeSeleccion } from "@/components/referencias/PanelDeSeleccion";
import { ScatterValorSuperficie } from "@/components/referencias/ScatterValorSuperficie";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import { NativeSelect } from "@/components/ui/native-select";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useCatalogoDeComparables,
  useDescartarFichaDelCatalogo,
  usePuntosDelCatalogo,
  useCatalogosDelPaso2,
  useEnlazarReferencias,
  useReferenciasDelInforme,
  useValidarFichaDelCatalogo,
} from "@/hooks/useReferencias";
import { useInforme } from "@/hooks/useInformes";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";
import { numero } from "@/lib/formatoDeMercado";
import { cn } from "@/lib/utils";
import {
  claveDeComparable,
  ETIQUETA_CLASE,
  type ClaseDeEvidencia,
  type Comparable,
  type OrdenDelCatalogo,
} from "@/lib/referencias";

/**
 * El mapa se carga PEREZOSO y el gráfico no.
 *
 * Leaflet pesa ~150 kB y recharts ya viaja en el bundle porque lo usa el
 * tablero. El webapp arrastra un trozo principal de más de 500 kB —anotado como
 * pendiente en el CLAUDE.md—, así que meter el mapa ahí se lo cobraría también
 * a quien nunca abre este módulo.
 */
const MapaDeComparables = lazy(() =>
  import("@/components/referencias/MapaDeComparables").then((modulo) => ({
    default: modulo.MapaDeComparables,
  })),
);

/** El lote del listado. Se ACUMULA, no pagina: la grilla se recorre entera. */
const LOTE = 20;

/**
 * Cuántas fichas acepta el servidor por pedido
 * (`CriteriosDeBusqueda::MAXIMO_POR_PAGINA`). Como el lote se acumula, pasado
 * este techo "Cargar más" recibiría un 422 en vez de fichas: ahí se deja de
 * ofrecer y se pide acotar el filtro.
 */
const TOPE_DEL_LISTADO = 100;

/**
 * El techo del filtro de superficie, en hectáreas, cuando el informe no declara
 * la suya.
 *
 * NO ES UN TOPE FIJO: la pantalla siembra la banda del predio de la mitad al
 * doble, así que con un predio de 145 ha el extremo son 291 — y un control
 * quemado en 200 lo recortaba sin decirlo, dejando fuera comparables que el
 * filtro decía incluir.
 */
const SUPERFICIE_MAX_POR_DEFECTO = 200;

/**
 * Lo que la url lleva cuando una manija está en su EXTREMO: "sin filtro".
 *
 * NO se borra la clave, y es lo que hace falta para que sobreviva a una
 * recarga. La siembra decide "ya hay un rango elegido" mirando si la clave
 * EXISTE (`parametros.has`); sin ella, un enlace con los rangos ensanchados
 * sería idéntico a uno recién abierto y volvería a imponer la banda de
 * superficie y los tres años. `leerNumero` no lo lee como número, así que
 * `leerFiltros` lo trata como ausente y el servidor nunca lo recibe.
 *
 * La banda de valor y la distancia no lo necesitan: la siembra no las toca.
 */
const SIN_TOPE = "sin_tope";

/**
 * La banda se siembra en HECTÁREAS ENTERAS, hacia afuera.
 *
 * El control de superficie avanza de a una hectárea y muestra el número sin
 * decimales, así que una banda de 18,7 a 74,8 se leería "19 – 75 ha" mientras
 * el servidor recibe otra cosa: el filtro diría un rango y buscaría otro.
 *
 * Se ensancha —piso hacia abajo, techo hacia arriba— y nunca al revés: el
 * redondeo no puede ser el motivo por el que un comparable del borde no
 * aparezca.
 */
function bandaEnHectareasEnteras(superficie: number): [number, number] {
  return [Math.floor(superficie / 2), Math.ceil(superficie * 2)];
}

/** El radio máximo que se puede pedir, en kilómetros. */
const DISTANCIA_MAX = 30;

/**
 * Paso 2: comparar el predio contra el mercado y llevarse referencias.
 *
 * TRES DECISIONES QUE NO SE VEN EN EL MARKUP
 *
 * 1. LA SELECCIÓN ES POR PESTAÑA Y VIVE EN MEMORIA, no en la url. Son dos
 *    conjuntos de evidencia distintos —un precio pagado y un precio pedido— y
 *    el tasador los arma por separado; pero es la elección de un momento, y un
 *    enlace que dijera "además tenía marcadas estas seis" no le sirve a nadie.
 *    Los FILTROS sí van en la url, como en el resto de los listados.
 *
 * 2. EL FILTRO DE DISTANCIA EXIGE COMUNA, y el control se muestra apagado
 *    hasta que haya una. No es una restricción de producto: en la base de
 *    comparables la geometría de una transacción es un `coalesce` sobre dos
 *    LEFT JOIN, así que un filtro por radio sin acotar antes no toca ningún
 *    índice espacial y recorre millones de filas. El servidor se niega con un
 *    422; la pantalla lo dice antes, para que nadie mueva un control que va a
 *    fallar.
 *
 * 3. ACÁ SE VALIDA Y SE DESCARTA, sobre la ficha del catálogo y sin agregarla
 *    al informe. Hasta el 08-09-2026 las dos acciones existían SOLO sobre una
 *    referencia ya enlazada, y eso invertía el orden del trabajo: para sacar
 *    del análisis un aviso repetido había que meterlo antes en la tasación,
 *    que es justo donde no se lo quiere. La revisión ocurre MIENTRAS se
 *    recorre el listado, no después.
 *
 *    Dos consecuencias que el markup no delata:
 *
 *    - VALIDADA NO ES DECIDIDA. La ficha validada SIGUE en el listado: lo que
 *      la saca es enlazarla o descartarla, nunca la marca. Si validar la
 *      escondiera, desaparecería en el mismo clic con que se la dio por buena.
 *    - EL MODAL DE DESCARTE VIVE ACÁ y no en la pantalla de enlazadas, y la
 *      asimetría no es un olvido: acá la ficha DESAPARECE de la lista que la
 *      persona está leyendo y se recalculan el total, la mediana y la
 *      paginación. Allá se mueve a una papelera que está en la pestaña de al
 *      lado, con su contador a la vista.
 */
export function ComparacionConMercado() {
  const { informeId } = useParams();
  const id = Number(informeId);

  const [parametros, setParametros] = useSearchParams();
  const clase = leerClase(parametros.get("clase"));

  const informe = useInforme(id);
  /* Las OPCIONES de los filtros. Se declara acá arriba, junto al informe,
     porque las dos son la ENTRADA de la siembra de abajo — no su consecuencia:
     de ellas sale qué comunas tiene el catálogo con las que cruzar. */
  const incluirIncompletas = leerIncluirIncompletas(parametros);
  const catalogos = useCatalogosDelPaso2(
    Number.isFinite(id) && id > 0 ? id : null,
    clase,
    incluirIncompletas,
  );
  const tieneCoordenada =
    informe.data?.latitud !== null && informe.data?.latitud !== undefined;

  const filtros = leerFiltros(parametros, tieneCoordenada, catalogos.data?.comunas);
  const [lotes, setLotes] = useState(1);

  /**
   * La pantalla ARRANCA ACOTADA A LA COMUNA DEL PREDIO.
   *
   * Sin esto abría con el catálogo ENTERO —unas 74.000 fichas de todo Chile— y
   * mostraba las seis primeras en el orden que fuera. Esas seis no tienen nada
   * que ver con el predio, y acá eso no es solo ruido: lo que se enlaza se
   * CONGELA dentro de un informe que se firma.
   *
   * La comuna sale de los ROLES del informe, que es donde vive
   * (`informe_roles.comuna_codigo`). Con roles en dos comunas se siembran las
   * dos, porque el filtro es múltiple.
   *
   * CUATRO CUIDADOS, y son el motivo de que esto sea un efecto y no un valor
   * por defecto dentro de `leerFiltros`:
   *
   *  1. SE SIEMBRA UNA SOLA VEZ. Si se recalculara, quitar la comuna a mano
   *     sería imposible: volvería sola en el render siguiente.
   *  2. SE ESCRIBE EN LA URL, como cualquier otro filtro. Así el control la
   *     muestra puesta, "Limpiar filtros" la limpia y el enlace se comparte
   *     igual. Un filtro invisible haría creer que el catálogo es chico.
   *  3. LA BANDERA VA EN ESTADO Y NO EN UN `useRef`. La pantalla tiene que
   *     volver a dibujarse cuando la siembra termina, porque de esa decisión
   *     depende cuándo se consulta el catálogo; un ref no dispara render y la
   *     pantalla se quedaba esperando para siempre.
   *
   *     PERO SON DOS BANDERAS, y la segunda SÍ es un ref. `siembraResuelta`
   *     significa "la url ya es la definitiva", no "ya decidí qué sembrar", y
   *     esa diferencia es un viaje al servidor: `setParametros` no se ve en el
   *     mismo pase, así que marcar resuelta junto a él dejaba salir una
   *     consulta SIN filtros —el catálogo entero, las ~74.000 fichas— y recién
   *     después la acotada. Lo que la siembra existe para evitar. El ref dice
   *     "ya escribí" y no necesita render; el estado se marca en el pase
   *     siguiente, cuando `parametros` ya trae lo sembrado.
   *  4. NO SE VALIDA CONTRA LAS COMUNAS DEL CATÁLOGO, y se probó al revés
   *     primero. El puente con la base de comparables es por NOMBRE y cubre
   *     343 de 346 comunas, así que sembrar una que no está abre la tabla
   *     vacía. Pero comprobarlo obliga a esperar a `catalogos` ANTES de pedir
   *     el listado —un viaje más en cada carga, para todos— y lo que evita es
   *     un caso del 1 % donde además el vacío dice la verdad: no hay
   *     comparables con ese filtro, y se quita con un clic.
   *
   *     EN EL CBR ESE 1 % ES CASI SIEMPRE, porque cubre solo cuatro comunas.
   *     No se arregla acá sino al LEER: `leerFiltros` ignora las comunas que
   *     la pestaña no tiene. La siembra sigue escribiendo la del predio, que
   *     es la correcta para Ofertas.
   */
  const [siembraResuelta, setSiembraResuelta] = useState(false);
  const yaSeSembro = useRef(false);

  useEffect(() => {
    if (siembraResuelta) return;

    if (yaSeSembro.current) {
      // El pase anterior escribió los filtros; `parametros` ya los trae. Recién
      // ahora la url es la definitiva y se puede consultar.
      setSiembraResuelta(true);

      return;
    }

    if (parametros.getAll("comunas").length > 0) {
      // Ya viene elegida (un enlace compartido, o el botón atrás): no se pisa.
      setSiembraResuelta(true);

      return;
    }

    const roles = informe.data?.roles;
    const aniosDelCatalogo = catalogos.data?.anios;
    if (!roles || !aniosDelCatalogo) return;

    const siguientes = new URLSearchParams(parametros);
    let hayQueSembrar = false;

    // --- La comuna del predio ---------------------------------------------
    const comunas = [
      ...new Set(
        roles
          .map((rol) => rol.comuna_codigo)
          .filter((codigo): codigo is string => codigo !== null),
      ),
    ];
    for (const codigo of comunas) siguientes.append("comunas", codigo);
    hayQueSembrar ||= comunas.length > 0;

    // --- Los últimos tres años --------------------------------------------
    //
    // La evidencia de mercado ENVEJECE: una venta de 2018 dice poco del valor
    // de hoy. Medido sobre la base real, tres años conservan el 81 % de las
    // ofertas y 13.897 transacciones — de sobra para comparar, y sin arrastrar
    // ocho años de historia que el tasador tendría que descartar a mano.
    //
    // SALEN DEL CATÁLOGO, no del calendario: si se sembrara "2026, 2025, 2024"
    // a ciegas y el catálogo no tuviera 2026, quedaría un filtro puesto sobre
    // años que no existen — invisible, como pasó con el código de comuna.
    //
    // Y ES UN RANGO, no una lista: `slice(0, 3)` sobre un catálogo de dos años
    // devuelve dos, así que el borde de abajo es el ÚLTIMO del recorte y nunca
    // `[2]`, que sería `undefined` y viajaría como `NaN`.
    //
    // Y NO SE PISA EL RANGO QUE YA VENGA EN LA URL, por lo mismo que no se pisa
    // la comuna: un enlace compartido lleva la elección de quien lo mandó. Con
    // la lista de años esto no hacía falta —se agregaban a lo que hubiera— pero
    // un rango se ESCRIBE, así que sembrar encima lo reemplazaría.
    //
    // "HAY RANGO" ES QUE LA CLAVE EXISTA, y no que tenga un número: un extremo
    // se guarda como `sin_tope` justamente para que esta pregunta siga dando
    // que sí después de ensanchar el rango hasta los bordes del catálogo.
    const yaHayRango = parametros.has("anio_desde") || parametros.has("anio_hasta");
    const ultimosTres = aniosDelCatalogo.slice(0, 3);
    if (!yaHayRango && ultimosTres.length > 0) {
      siguientes.set("anio_desde", String(ultimosTres[ultimosTres.length - 1]));
      siguientes.set("anio_hasta", String(ultimosTres[0]));
      hayQueSembrar = true;
    }

    // --- La superficie del predio, de la MITAD AL DOBLE --------------------
    //
    // NO ±10 %, y no es una preferencia: está medido. Para un predio de 145,75
    // ha, transacciones desde 2024, ±10 % deja CERO comparables; ±25 %, uno.
    // El catálogo de transacciones es de propiedades chicas —el 96 % mide menos
    // de 1 ha, mediana 300 m²— y solo 41 en todo el país superan las 50 ha.
    //
    // Un porcentaje FIJO falla distinto según el tamaño: con 0,5 ha, ±10 % deja
    // 3.103 transacciones; con 50 ha, una. "De la mitad al doble" es el único
    // rango que sostiene números usables en todo el espectro (74 ofertas para
    // ese predio de 145 ha, contra 13 con ±10 %).
    //
    // Y se puede ser generoso porque la pantalla compara por valor UNITARIO
    // (UF/ha) contra la mediana del filtro: predios de tamaños distintos se
    // comparan bien justamente porque el precio va normalizado por hectárea.
    //
    // Tampoco se pisa una banda que ya venga en la url, `sin_tope` incluido: es
    // la misma regla que el rango de años. Antes esta rama sembraba siempre, y
    // quien ensanchaba la superficie hasta los extremos —sin comuna en la url—
    // la recuperaba acotada al recargar.
    //
    // Un borde que cae en el extremo de la pista se siembra como `sin_tope`: el
    // control lo dibuja pegado al tope, y escribirlo como número sería un filtro
    // que él mismo niega aplicar (ver `SIN_TOPE`).
    const superficie = Number(informe.data?.superficie_ha);
    const yaHayBanda = parametros.has("superficie_min") || parametros.has("superficie_max");
    if (!yaHayBanda && Number.isFinite(superficie) && superficie > 0) {
      const [minimo, maximo] = bandaEnHectareasEnteras(superficie);
      siguientes.set("superficie_min", minimo <= 0 ? SIN_TOPE : String(minimo));
      siguientes.set(
        "superficie_max",
        maximo >= SUPERFICIE_MAX_POR_DEFECTO ? SIN_TOPE : String(maximo),
      );
      hayQueSembrar = true;
    }

    if (!hayQueSembrar) {
      // No hay nada que escribir, así que la url ya es la definitiva.
      setSiembraResuelta(true);

      return;
    }

    yaSeSembro.current = true;
    setParametros(siguientes, { replace: true });
  }, [siembraResuelta, informe.data, catalogos.data, parametros, setParametros]);

  /**
   * ¿El filtro está acotado a algún territorio?
   *
   * SIN COMUNA SE LISTA EL PAÍS ENTERO, Y SE AVISA. Antes esto era una
   * NEGATIVA: sin comuna la pantalla no consultaba el catálogo y pedía elegir
   * una. La negativa se retiró (07-09-2026) porque le daba al mismo estado un
   * significado OPUESTO al del control que lo produce: para `SelectorMultiple`
   * la selección vacía significa "todas" —su botón "Todas" limpia, y el resumen
   * dice "Todas · 255"— mientras la pantalla lo leía como "ninguna" y se
   * apagaba. Quien apretaba "Todas" se quedaba sin datos y sin explicación.
   *
   * Lo que la negativa protegía NO se perdió, cambió de forma: que estos
   * comparables pueden estar a cientos de kilómetros del predio, y que lo que
   * se enlaza se CONGELA en un documento que se firma. Eso ahora se dice, que
   * es lo que el tasador necesita para decidir. Impedirlo, además, dejaba sin
   * ninguna salida al informe que no tiene comuna que sembrar.
   *
   * El único filtro que SIGUE exigiendo comuna es el de distancia, y no por
   * criterio de producto: el servidor responde 422 porque el índice espacial no
   * alcanza a la rama de transacciones. Ese control se apaga solo y lo explica.
   */
  const sinComuna = siembraResuelta && filtros.comunas.length === 0;

  // Un conjunto de selección POR CLASE: cambiar de pestaña conserva lo elegido
  // en la otra, que es como se arma un informe con las dos evidencias.
  const [seleccion, setSeleccion] = useState<Record<string, Comparable>>({});

  // NO SE CONSULTA EL CATÁLOGO HASTA DECIDIR LA SIEMBRA, y eso es la mitad del
  // arreglo: consultando antes se pedían igual las ~74.000 fichas una vez, y
  // recién después llegaba el filtro. El usuario veía el catálogo entero por un
  // instante y el servidor hacía el trabajo de todos modos.
  const pedidas = Math.min(LOTE * lotes, TOPE_DEL_LISTADO);
  const catalogo = useCatalogoDeComparables(
    Number.isFinite(id) && id > 0 && siembraResuelta ? id : null,
    { ...filtros, clase, porPagina: pedidas },
  );
  // La NUBE del mercado: todo el filtro, no la página. Va en su propia consulta
  // porque no depende de la paginación — pedir "cargar más" agrega veinte fichas a
  // la tabla y no cambia ni un punto del mapa.
  const puntos = usePuntosDelCatalogo(
    Number.isFinite(id) && id > 0 && siembraResuelta ? id : null,
    { ...filtros, clase },
  );

  const enlazadas = useReferenciasDelInforme(Number.isFinite(id) && id > 0 ? id : null);
  const enlazar = useEnlazarReferencias(id, incluirIncompletas);

  // Las dos decisiones que se toman sobre una ficha SIN agregarla al informe.
  const validarFicha = useValidarFichaDelCatalogo(id);
  const descartarFicha = useDescartarFichaDelCatalogo(id);

  /*
   * La ficha que espera confirmación para irse a la papelera.
   *
   * SE GUARDA LA FICHA ENTERA Y NO SU CLAVE: el modal la nombra —"la que se va
   * es esta y no la de al lado" es lo único que ese diálogo aporta—, y con la
   * clave habría que volver a buscarla en un listado que la propia confirmación
   * está por cambiar.
   */
  const [porDescartar, setPorDescartar] = useState<Comparable | null>(null);

  const estado = estadoDeConsulta(catalogo);

  /**
   * ¿El CONJUNTO se está recalculando, o solo se están pidiendo más fichas?
   *
   * Las dos consultas conservan el resultado anterior mientras viaja el nuevo
   * (`placeholderData`), y esa decisión está tomada a conciencia: un mapa que
   * se vacía se lee como "no hay comparables" y no como "estoy cargando". Pero
   * conservarlo sin decirlo deja en pantalla el conteo, la mediana, el gráfico,
   * el mapa y las fichas del filtro ANTERIOR como si fueran la respuesta.
   *
   * SE MIRAN LOS PUNTOS Y NO EL CATÁLOGO, y ahí está toda la distinción: la
   * clave de los puntos es exactamente el filtro SIN la paginación, así que se
   * vuelven a pedir cuando cambia el filtro y NO cuando alguien aprieta "Cargar
   * más". En ese segundo caso lo que se ve sigue siendo cierto —solo está
   * incompleto—, así que la franja mentiría y el que avisa es el botón.
   *
   * Que además sea la consulta más LENTA de las dos (~600 ms contra los ~80 del
   * conteo, medido) hace que su ventana cubra a la del catálogo casi siempre.
   */
  const recalculandoElConjunto = estadoDeConsulta(puntos).refrescando;

  /**
   * El total que imprime el pie de la barra de filtros.
   *
   * VUELVE A "NO SE SABE" MIENTRAS SE RECALCULA, y es el dato más engañoso de
   * los que sobrevivían: un conteo nítido pegado a los controles que se acaban
   * de mover se lee como el resultado de moverlos. El pie ya tenía escrito qué
   * decir en ese caso ("Buscando comparables…"); con el resultado anterior
   * conservado ese `null` no llegaba nunca, y la frase era código muerto.
   */
  const totalDelFiltro = recalculandoElConjunto ? null : (catalogo.data?.total ?? null);

  const fichas = catalogo.data?.fichas ?? [];
  const maximoUfHa = useMemo(() => {
    const valores = fichas
      .map((f) => f.uf_ha_terreno)
      .filter((v): v is number => v !== null && v > 0);

    return valores.length === 0 ? null : Math.max(...valores);
  }, [fichas]);

  const seleccionadasDeLaClase = Object.values(seleccion).filter((f) => f.clase === clase);

  /*
   * Qué fichas de esta pestaña ya se dieron por buenas.
   *
   * `Set` y no el arreglo: la tarjeta pregunta una vez por ficha, y con
   * `includes` sobre una lista que crece toda la tarde eso es un recorrido por
   * cada tarjeta en cada render.
   */
  const validadas = useMemo(
    () => new Set(catalogo.data?.validadas ?? []),
    [catalogo.data?.validadas],
  );

  // Lo que la bajada del listado informa: cuántas de las que están A LA VISTA
  // faltan por revisar. Sobre las cargadas y no sobre el total del filtro,
  // porque es lo que la persona tiene delante para hacer algo al respecto.
  const sinValidar = fichas.filter((f) => !validadas.has(f.origen_id)).length;

  const enLaPapelera = catalogo.data?.enLaPapelera ?? 0;

  /**
   * Confirmar el descarte.
   *
   * DESELECCIONA ADEMÁS DE DESCARTAR, y no es un detalle: la ficha sale del
   * listado, así que si quedara marcada se iría al informe en el próximo
   * "Agregar" sin estar en ninguna pantalla donde desmarcarla. El servidor no
   * puede hacerlo — la selección vive en el navegador hasta que se confirma.
   */
  const confirmarDescarte = () => {
    if (porDescartar === null) return;

    const clave = claveDeComparable(porDescartar);

    descartarFicha.mutate(porDescartar);
    setSeleccion((actual) => {
      const { [clave]: _descartada, ...resto } = actual;

      return resto;
    });
    setPorDescartar(null);
  };

  /**
   * La superficie del PREDIO TASADO, en hectáreas.
   *
   * El panel la multiplica por la mediana elegida. Llega como string desde el
   * API y puede faltar —un informe `incompleto` (RF-E1 §8.3)—, así que lo que
   * no sea un número positivo vale `null`: multiplicar por cero daría "0 UF",
   * que se lee como una medición en vez de como el dato que falta.
   */
  const superficieDelPredio = (() => {
    const valor = Number(informe.data?.superficie_ha);

    return Number.isFinite(valor) && valor > 0 ? valor : null;
  })();

  /* LAS DOS ESCALAS DE LOS EJES DEL GRÁFICO.

     Subieron desde la barra de filtros el 09-09-2026, cuando los dos controles
     se mudaron a los ejes: acá las necesita el gráfico, que es quien los dibuja.

     HASTA DÓNDE LLEGA LA PISTA DE SUPERFICIE, y sale de la BANDA SEMBRADA —
     nunca de lo que el filtro tiene puesto. Salía del propio valor, y eso hacía
     que la escala se ENCOGIERA con cada elección: bajar el máximo de 1.862 a
     1.547 ha dejaba la pista terminando en 1.547, con la manija pegada a su
     borde derecho y sin camino de vuelta. Un control no puede borrar los
     valores que él mismo ofrece. El extremo puesto sigue siendo un PISO, para
     que un enlace compartido con un máximo por encima de la escala no deje la
     manija fuera de su pista. */
  const techoDeSuperficie = Math.max(
    SUPERFICIE_MAX_POR_DEFECTO,
    superficieDelPredio ? bandaEnHectareasEnteras(superficieDelPredio)[1] : 0,
    Math.ceil(filtros.superficieMaxHa ?? 0),
  );

  /* La MISMA regla para el valor unitario, y acá el techo no tiene default
     posible: lo manda el servidor porque depende del mercado que el filtro
     describe, y lo mide SIN la banda puesta justo para que la pista no se
     encoja bajo la manija. Ver `escalaDeValorUnitario()` en el catálogo. */
  const escalaUfHa = puntos.data?.escalaUfHa ?? null;
  const techoDeValor = Math.max(escalaUfHa ?? 0, Math.ceil(filtros.valorMaxUfHa ?? 0));

  // Un `Set` de CLAVES y no de objetos: el gráfico y el mapa preguntan "¿está
  // elegida?" una vez por punto, y comparar objetos ahí sería una búsqueda
  // lineal por cada uno.
  const clavesSeleccionadas = useMemo(
    () => new Set(Object.keys(seleccion)),
    [seleccion],
  );

  /**
   * Cambiar un filtro reinicia los lotes pero NO la selección.
   *
   * Al revés que el paso 1, donde cambiar el filtro limpiaba el informe
   * elegido. Acá lo que se está armando es un conjunto, y el usuario filtra
   * justamente para ir a buscar la siguiente ficha: borrarle lo que ya juntó
   * convertiría el filtro en una trampa.
   */
  const cambiarFiltro = (clave: string, valor: string | null) => {
    const siguientes = new URLSearchParams(parametros);
    if (valor === null || valor === "") siguientes.delete(clave);
    else siguientes.set(clave, valor);
    setParametros(siguientes, { replace: true });
    setLotes(1);
  };

  const cambiarLista = (clave: string, valores: string[]) => {
    const siguientes = new URLSearchParams(parametros);
    siguientes.delete(clave);
    for (const valor of valores) siguientes.append(clave, valor);
    setParametros(siguientes, { replace: true });
    setLotes(1);
  };

  /**
   * Cambiar de pestaña SUELTA los filtros que la otra clase no puede deshacer.
   *
   * Hoy es uno solo: la clase de uso de suelo, que existe únicamente en
   * transacciones —ninguna oferta tiene rol, así que ninguna llega a un predio—
   * y cuyo control por eso se OCULTA en la pestaña de ofertas. Esconderlo no
   * alcanza: el parámetro seguía en la url, `leerFiltros` lo leía igual y la
   * consulta se iba con él, así que la pestaña volvía SIN UNA SOLA FICHA, sin
   * explicación y sin ningún control en pantalla con el que sacarlo.
   *
   * LA COMUNA, EL AÑO Y LA SUPERFICIE SÍ CRUZAN, y eso no es inconsistencia:
   * significan lo mismo en las dos pestañas. Las FUENTES tampoco se sueltan
   * aunque sus claves no se repitan entre clases —una `cbr_*` no existe entre
   * las ofertas— porque ahí el selector SE VE y se limpia de un clic. Lo que
   * hace falta soltar no es lo que no aplica: es lo que no se puede deshacer.
   *
   * VA EN UNA SOLA ESCRITURA, misma regla que `cambiarRango`: dos llamadas
   * seguidas a `cambiarFiltro` parten las dos de la url del render anterior, y
   * la segunda pisaría a la primera sin que nada falle.
   */
  const cambiarLaClase = (valor: string) => {
    const siguientes = new URLSearchParams(parametros);
    siguientes.set("clase", valor);
    if (valor !== "transaccion") siguientes.delete("clases_de_suelo");
    setParametros(siguientes, { replace: true });
    setLotes(1);
  };

  /**
   * Los dos extremos de un rango, en UNA escritura.
   *
   * Dos llamadas seguidas a `cambiarFiltro` parten las dos de la url del
   * render anterior, así que la segunda pisa a la primera y el mínimo se
   * pierde sin que nada falle.
   *
   * `null` BORRA la clave. Es lo que deja expresar "sin tope", que no es lo
   * mismo que un tope igual al extremo de la pista: la escala del valor
   * unitario se redondea hacia arriba, así que escribir su extremo filtraría
   * por una cifra que nadie eligió.
   *
   * Superficie y año, en cambio, escriben `SIN_TOPE` en el extremo y no borran:
   * necesitan que la clave siga en la url para que la siembra no vuelva a
   * imponerles su rango al recargar.
   */
  const cambiarRango = (cambios: Record<string, number | string | null>) => {
    const siguientes = new URLSearchParams(parametros);
    for (const [clave, valor] of Object.entries(cambios)) {
      if (valor === null) siguientes.delete(clave);
      else siguientes.set(clave, String(valor));
    }
    setParametros(siguientes, { replace: true });
    setLotes(1);
  };

  const alternar = (ficha: Comparable) => {
    const clave = claveDeComparable(ficha);
    setSeleccion((actual) => {
      const siguiente = { ...actual };
      if (siguiente[clave]) delete siguiente[clave];
      else siguiente[clave] = ficha;

      return siguiente;
    });
  };

  const agregar = () => {
    enlazar.mutate(
      seleccionadasDeLaClase.map((f) => ({ clase: f.clase, origen_id: f.origen_id })),
      {
        onSuccess: () => {
          // Solo se limpia lo que se acaba de enlazar. Lo elegido en la otra
          // pestaña sigue ahí: no se agregó y borrarlo perdería su trabajo.
          setSeleccion((actual) =>
            Object.fromEntries(
              Object.entries(actual).filter(([, f]) => f.clase !== clase),
            ),
          );
        },
      },
    );
  };

  if (informe.isError) {
    /* Va envuelto y no suelto: el error es la pantalla entera cuando ocurre, y
       sin el mismo padding quedaba a ras del borde del panel. */
    return (
      <div className="px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
        <EstadoDeError
          titulo="No se pudo abrir el informe"
          error={informe.error}
          onReintentar={informe.refetch}
          reintentando={informe.isFetching}
        />
      </div>
    );
  }

  return (
    /* `p-6 lg:p-8`: el mismo padding horizontal que `TopBar`, para que el
       cuerpo arranque en la vertical del título. Ver el paso 1. */
    <div className="flex flex-col gap-4 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <Encabezado
        id={id}
        nombre={informe.data?.propiedad_nombre ?? null}
        cargando={informe.isLoading}
      />

      <PredioTasado informe={informe.data} cargando={informe.isLoading} />

      {/* DOS COLUMNAS DE ARRIBA A ABAJO: a la izquierda el filtro con sus
          resultados, a la derecha lo que se lleva al informe.

          La grilla arrancaba recién en el gráfico, así que las pestañas, la
          barra de filtros y los dos avisos cruzaban la pantalla entera mientras
          el panel de la derecha empezaba a media altura. Eso dejaba a la barra
          MÁS ANCHA que el gráfico y el mapa que acota —dos anchos para lo mismo,
          uno debajo del otro— y mandaba el valor referencial, lo seleccionado y
          el botón de agregar abajo del pliegue: lo que se está construyendo
          quedaba fuera de la vista mientras se elige. */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_19.5rem] lg:items-start">
        <div className="flex min-w-0 flex-col gap-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <Tabs
              variant="underline"
              value={clase}
              onValueChange={cambiarLaClase}
            >
              <TabsList>
                {(["transaccion", "oferta"] as ClaseDeEvidencia[]).map((valor) => (
                  <TabsTrigger key={valor} value={valor}>
                    {ETIQUETA_CLASE[valor]}
                    {valor === clase && catalogo.data && (
                      <Badge variant="secondary" className="ml-2 font-mono">
                        {catalogo.data.total}
                      </Badge>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          <Filtros
            parametros={parametros}
            filtros={filtros}
            catalogos={catalogos.data}
            catalogosCargando={catalogos.isLoading}
            clase={clase}
            totalDelFiltro={totalDelFiltro}
            onCambioDeLista={cambiarLista}
            onCambioDeRango={cambiarRango}
            onCambioDeIncompletas={(incluir) =>
              cambiarFiltro("incluir_incompletas", incluir ? "1" : null)
            }
            onLimpiar={() => {
              setParametros(new URLSearchParams({ clase }), { replace: true });
              setLotes(1);
            }}
          />

          {/* EL AVISO QUE REEMPLAZÓ A LA NEGATIVA.

              Va acá —entre los filtros y todo lo demás— porque la condición no es
              de la lista: el gráfico, el mapa y la mediana se leen distinto cuando
              el conjunto es el país entero. Un aviso al pie de la tabla llegaría
              después de las tres lecturas que corrige.

              Es `warning` y no `info`: lo que advierte no es un detalle del filtro
              sino que estos comparables pueden no tener relación con el predio, y
              que lo que se enlace queda congelado en un informe que se firma. */}
          {sinComuna && (
            <div
              role="status"
              className="bg-warning-soft text-warning-soft-foreground flex items-start gap-2.5 rounded-xl px-4 py-3"
            >
              <Info className="mt-px size-4 flex-none" aria-hidden />
              <p className="text-caption">
                <span className="font-bold">Sin comuna, el filtro abarca todo el país.</span>{" "}
                Estos comparables pueden estar a cientos de kilómetros del predio, y lo que
                enlace queda dentro del informe. Elija una comuna para acotarlos.
              </p>
            </div>
          )}

          {/* LA FRANJA QUE DICE QUE ESTÁ TRABAJANDO.

              Va acá —entre los filtros y todo lo demás— por lo mismo que el aviso
              de arriba: lo que quedó viejo no es la lista sino el CONJUNTO, y el
              gráfico, el mapa y la mediana se leen antes de llegar a las fichas.

              Es UNA sola señal para las dos consultas. Un indicador por pieza —uno
              en el gráfico, otro en el mapa, otro sobre la tabla— repetiría cuatro
              veces el mismo hecho y ninguno diría lo único que importa: que nada de
              eso responde todavía al filtro que se acaba de mover. */}
          {recalculandoElConjunto && (
            <div
              role="status"
              className="bg-info-soft text-info-soft-foreground flex items-center gap-2.5 rounded-xl px-4 py-3"
            >
              <Spinner className="size-4 flex-none" aria-hidden />
              <p className="text-caption">
                <span className="font-bold">Actualizando los comparables…</span> Lo que se ve
                abajo todavía es el resultado del filtro anterior.
              </p>
            </div>
          )}

          {/* `aria-busy` y el atenuado son la MISMA afirmación por dos vías: la
              franja la lee quien mira ahí arriba, y esto marca todo lo de abajo
              para el resto —incluido un lector de pantalla, que si no cantaría
              como resultado un conteo que ya no corresponde al filtro puesto.

              NO se bloquean los clics: la selección se conserva a propósito al
              cambiar de filtro, así que elegir una ficha mientras llega la nueva
              tanda es válido, y una pantalla que deja de responder se lee como
              colgada. */}
          <section
            aria-label="Comparables del filtro"
            aria-busy={recalculandoElConjunto}
            className={cn(
              "flex flex-col gap-3 transition-opacity",
              recalculandoElConjunto && "opacity-60",
            )}
          >
            {/* Las dos lecturas del conjunto, antes de la lista: el gráfico dice
                si tiene forma y el mapa dónde cae.
                Dibujan DOS capas. El fondo es TODO el filtro —miles de fichas,
                agrupadas— porque es lo que da la forma del mercado; encima van
                las fichas CARGADAS, que son las que se pueden elegir. Antes solo
                existía la segunda, así que la "forma" se leía sobre seis puntos. */}
            <div className="grid gap-3 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
              {/* LOS DOS FILTROS DE RANGO VIVEN ACÁ, PEGADOS A SUS EJES, y no en
                  la barra de arriba. Acotar es mirar la nube y correr un borde:
                  con los controles a media pantalla de distancia, cada ajuste
                  costaba subir la vista, mover a ciegas y volver a bajar. Los
                  otros cuatro filtros se quedaron arriba porque no hablan de
                  ninguno de los dos ejes. */}
              <ScatterValorSuperficie
                fichas={fichas}
                seleccionadas={clavesSeleccionadas}
                onAlternar={alternar}
                medianaUfHa={catalogo.data?.medianaUfHa ?? null}
                mercado={puntos.data?.puntos}
                superficie={{
                  valor: [
                    filtros.superficieMinHa ?? 0,
                    filtros.superficieMaxHa ?? techoDeSuperficie,
                  ],
                  escala: techoDeSuperficie,
                  // Los DOS extremos en UNA escritura: con dos llamadas seguidas,
                  // cada una parte de la url del render anterior y la segunda pisa
                  // a la primera — el mínimo se perdía en silencio.
                  //
                  // Y CADA EXTREMO DE LA PISTA ES "SIN FILTRO": 0 abajo y el techo
                  // arriba escriben `SIN_TOPE`. Como número, el servidor lo volvía
                  // `BETWEEN` y descartaba toda ficha sin superficie publicada y
                  // todo predio por encima de la pista.
                  onCambio: ([min, max]) =>
                    cambiarRango({
                      superficie_min: min <= 0 ? SIN_TOPE : min,
                      superficie_max: max >= techoDeSuperficie ? SIN_TOPE : max,
                    }),
                }}
                valorUnitario={{
                  valor: [filtros.valorMinUfHa ?? 0, filtros.valorMaxUfHa ?? techoDeValor],
                  escala: techoDeValor,
                  paso: pasoDeLaEscala(techoDeValor),
                  deshabilitado: techoDeValor <= 0,
                  // LA MANIJA EN EL TOPE SIGNIFICA "SIN LÍMITE", y por eso borra la
                  // clave en vez de escribirla. La escala se redondea HACIA ARRIBA,
                  // así que su extremo casi nunca es un valor real del catálogo:
                  // mandarlo filtraría por una cifra que nadie eligió, y bastaría
                  // el redondeo para esconder la ficha más cara del conjunto.
                  onCambio: ([min, max]) =>
                    cambiarRango({
                      valor_min_uf_ha: min <= 0 ? null : min,
                      valor_max_uf_ha: max >= techoDeValor ? null : max,
                    }),
                }}
              />

              <Suspense
                fallback={
                  <Skeleton className="h-72 w-full rounded-xl" />
                }
              >
                <MapaDeComparables
                  fichas={fichas}
                  seleccionadas={clavesSeleccionadas}
                  onAlternar={alternar}
                  predio={
                    tieneCoordenada && informe.data
                      ? {
                          latitud: Number(informe.data.latitud),
                          longitud: Number(informe.data.longitud),
                        }
                      : null
                  }
                  mercado={puntos.data?.puntos}
                  totalDelMercado={puntos.data?.total}
                  mercadoTruncado={puntos.data?.truncado}
                  filtroDeDistancia={{
                    km: filtros.distanciaMaxKm ?? DISTANCIA_MAX,
                    max: DISTANCIA_MAX,
                    // EN EL TOPE SE BORRA LA CLAVE. La manija arriba del todo
                    // lee "sin tope", así que escribir el extremo mandaría un
                    // radio de 30 km que nadie eligió, en un catálogo nacional.
                    onCambio: (km) =>
                      cambiarFiltro(
                        "distancia_max",
                        km >= DISTANCIA_MAX ? null : String(km),
                      ),
                    deshabilitado: !(tieneCoordenada && filtros.comunas.length > 0),
                    motivo: tieneCoordenada
                      ? "Elija al menos una comuna para poder filtrar por distancia."
                      : "Este informe no tiene marcada la ubicación del predio.",
                  }}
                  /* El radio del filtro, dibujado sobre el predio. Sin tope
                     —o sin comuna, o sin coordenada— el filtro no trae distancia
                     y el mapa no dibuja ningún círculo. */
                  radioKm={filtros.distanciaMaxKm ?? null}
                />
              </Suspense>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-ink text-h3">
                  {catalogo.data
                    ? `${catalogo.data.total} ${clase === "transaccion" ? "transacciones comparables" : "ofertas comparables"}`
                    : "Buscando comparables"}
                </h2>
                {/* El avance de la revisión va PRIMERO y la explicación de la
                    barra después: lo segundo se lee una vez y lo primero cambia
                    con cada clic. */}
                <p className="text-ink-hint text-caption">
                  {fichas.length > 0 && (
                    <>
                      {sinValidar === 0 ? "Todas validadas" : `${sinValidar} sin validar`}
                      {" · "}
                    </>
                  )}
                  {fichas.length > 0 ? "la" : "La"} barra compara cada ficha contra la mediana
                  del filtro
                  {catalogo.data?.medianaUfHa !== null && catalogo.data
                    ? `: ${numero(catalogo.data.medianaUfHa, 0)} UF/ha`
                    : ""}
                </p>
              </div>

              <div className="flex items-end gap-2">
                <NativeSelect
                  aria-label="Ordenar por"
                  value={filtros.orden ?? ""}
                  onChange={(evento) => cambiarFiltro("orden", evento.target.value)}
                  className="w-52"
                >
                  {(catalogos.data?.ordenes ?? []).map((orden) => (
                    <option key={orden.clave} value={orden.clave}>
                      {orden.etiqueta}
                    </option>
                  ))}
                </NativeSelect>

                <Button
                  variant="outline"
                  onClick={() =>
                    setSeleccion((actual) => ({
                      ...actual,
                      ...Object.fromEntries(fichas.map((f) => [claveDeComparable(f), f])),
                    }))
                  }
                  disabled={fichas.length === 0}
                >
                  Seleccionar todas
                </Button>

                {/* SE OCULTA ENTERO CUANDO NO HAY NADA DESCARTADO, no se
                    deshabilita: una papelera vacía no es una acción bloqueada, es
                    una puerta que todavía no existe. Y va como ENLACE subrayado y
                    no como botón — es una salida a otra pantalla, no una acción
                    sobre el listado, y un tercer botón acá competiría con
                    "Agregar al informe", que es lo que la persona vino a hacer. */}
                {enLaPapelera > 0 && (
                  <Link
                    to={`/referencias/${id}/enlazadas?vista=papelera`}
                    className="text-ink-muted hover:text-danger-soft-foreground decoration-border hover:decoration-danger-soft-foreground/40 mb-2.5 inline-flex items-center gap-1.5 text-xs font-bold underline underline-offset-[3px] transition-colors"
                  >
                    <Trash2 className="size-3.5" />
                    Papelera ({enLaPapelera})
                  </Link>
                )}
              </div>
            </div>

            {estado.sinConexion && <EstadoSinConexion que="el catálogo de mercado" />}

            {estado.hayError && (
              <EstadoDeError
                titulo="No se pudo consultar el catálogo de mercado"
                error={catalogo.error}
                onReintentar={catalogo.refetch}
                reintentando={catalogo.isFetching}
              />
            )}

            {estado.cargando && (
              <div className="flex flex-col gap-3">
                {Array.from({ length: 3 }, (_, i) => (
                  <Skeleton key={i} className="h-44 w-full rounded-xl" />
                ))}
              </div>
            )}

            {!estado.cargando &&
              !estado.hayError &&
              !estado.sinConexion &&
              fichas.length === 0 && (
                <Empty>
                  <EmptyHeader>
                    <EmptyTitle>Ningún comparable cumple estos filtros</EmptyTitle>
                    <EmptyDescription>
                      Pruebe ampliar la superficie, los años o la distancia.
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              )}

            {fichas.map((ficha, indice) => (
              <FichaDeComparable
                key={claveDeComparable(ficha)}
                ficha={ficha}
                numeroDeOrden={indice + 1}
                seleccionada={Boolean(seleccion[claveDeComparable(ficha)])}
                onAlternar={() => alternar(ficha)}
                medianaUfHa={catalogo.data?.medianaUfHa ?? null}
                maximoUfHa={maximoUfHa}
                validada={validadas.has(ficha.origen_id)}
                onValidar={(validada) => validarFicha.mutate({ ficha, validada })}
                onDescartar={() => setPorDescartar(ficha)}
                decidiendo={validarFicha.isPending || descartarFicha.isPending}
              />
            ))}

            {catalogo.data && catalogo.data.quedan > 0 && pedidas >= TOPE_DEL_LISTADO && (
              <p className="text-ink-hint pt-1 text-center text-xs">
                Se muestran hasta {TOPE_DEL_LISTADO} comparables. Acota los filtros para ver
                los {catalogo.data.quedan} restantes.
              </p>
            )}
            {catalogo.data && catalogo.data.quedan > 0 && pedidas < TOPE_DEL_LISTADO && (
              <div className="flex flex-col items-center gap-1.5 pt-1">
                {/* PEDIR MÁS FICHAS NO LEVANTA LA FRANJA, lo dice el botón.

                    Acá lo que hay en pantalla no está mintiendo —son las fichas
                    del filtro puesto, solo que faltan— así que el aviso de arriba
                    no corresponde. Lo que sí faltaba es que el botón acusara el
                    clic: sin esto se queda igual medio segundo y se aprieta de
                    nuevo, pidiendo un lote más del que nadie quería. */}
                <Button
                  variant="outline"
                  onClick={() => setLotes((n) => n + 1)}
                  disabled={catalogo.isFetching}
                >
                  {catalogo.isFetching && <Spinner aria-hidden />}
                  Cargar{" "}
                  {Math.min(LOTE, catalogo.data.quedan, TOPE_DEL_LISTADO - pedidas)} más
                </Button>
                <p className="text-ink-hint font-mono text-xs tabular-nums">
                  {fichas.length} de {catalogo.data.total} · quedan {catalogo.data.quedan}
                </p>
              </div>
            )}

            <p className="text-ink-hint text-caption pt-1">
              Un campo vacío trae su motivo — «no publicado» no es lo mismo que cero, y el
              sistema no guarda datos de contacto del vendedor.
            </p>
          </section>
        </div>

        <PanelDeSeleccion
          seleccionadas={seleccionadasDeLaClase}
          onQuitar={alternar}
          onVaciar={() =>
            setSeleccion((actual) =>
              Object.fromEntries(
                Object.entries(actual).filter(([, f]) => f.clase !== clase),
              ),
            )
          }
          onAgregar={agregar}
          agregando={enlazar.isPending}
          yaEnlazadas={enlazadas.data?.total ?? 0}
          informeId={id}
          totalDelFiltro={catalogo.data?.total ?? 0}
          superficieDelPredioHa={superficieDelPredio}
          validadas={validadas}
        />
      </div>

      {/* LA ÚNICA CONFIRMACIÓN DE ESTA PANTALLA, y no porque descartar sea
          grave: es reversible y va a una papelera que está a un clic. Confirma
          porque la ficha DESAPARECE del listado — se recalculan el total, la
          mediana y la paginación—, y una acción que reordena lo que la persona
          está leyendo no puede dispararse desde un ícono de 30 px que está al
          lado del que valida.

          POR ESO EL BOTÓN ES NEGRO Y NO ROJO. En este módulo el color dice si
          la acción se deshace: neutro sí, rojo no. El rojo queda para vaciar la
          papelera, que es el único acto irreversible. */}
      <AlertDialog
        open={porDescartar !== null}
        onOpenChange={(abierto) => !abierto && setPorDescartar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Enviar a la papelera?</AlertDialogTitle>
            <AlertDialogDescription>
              La ficha sale del listado, del gráfico y de la selección, pero no se borra:
              queda en la papelera del informe y puede restaurarla mientras trabaje en él.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* CUÁL se va. Es lo único que este diálogo aporta que la pantalla no
              decía ya: el botón que se apretó está en una fila de seis fichas
              casi idénticas. */}
          {porDescartar !== null && (
            <div className="border-border bg-muted/40 flex flex-wrap items-center gap-2 rounded-lg border-[1.5px] px-3 py-2.5">
              {porDescartar.referencia && (
                <span className="bg-muted text-ink-muted rounded-md px-1.5 py-0.5 font-mono text-xs font-semibold">
                  {porDescartar.clase === "transaccion"
                    ? `Rol ${porDescartar.referencia}`
                    : porDescartar.referencia}
                </span>
              )}
              <span className="text-ink-body min-w-0 truncate text-xs font-bold">
                {porDescartar.titulo}
              </span>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: "secondary" })}
              onClick={confirmarDescarte}
            >
              Enviar a la papelera
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Encabezado({
  id,
  nombre,
  cargando,
}: {
  id: number;
  nombre: string | null;
  cargando: boolean;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <p className="text-ink-muted text-caption">
        Seleccione las transacciones y ofertas que van al informe. La homologación de cada
        una se realiza después, dentro del informe.
      </p>

      <div className="flex items-center gap-2">
        <Link to="/referencias">
          <Button variant="ghost">
            <ArrowLeft className="size-4" />
            Cambiar de informe
          </Button>
        </Link>
        <Link to={`/informes/${id}`}>
          <Button variant="outline" disabled={cargando}>
            Volver al informe
            {nombre && <span className="sr-only"> {nombre}</span>}
          </Button>
        </Link>
      </div>
    </div>
  );
}

/**
 * La franja del predio tasado.
 *
 * Está arriba de todo y siempre visible porque CADA ficha de abajo se lee
 * contra ella: la distancia, la superficie y el valor unitario no significan
 * nada sin saber contra qué se comparan.
 */
function PredioTasado({
  informe,
  cargando,
}: {
  informe: ReturnType<typeof useInforme>["data"];
  cargando: boolean;
}) {
  if (cargando) return <Skeleton className="h-20 w-full rounded-xl" />;
  if (!informe) return null;

  const comunas = [
    ...new Set((informe.roles ?? []).map((rol) => rol.comuna).filter(Boolean)),
  ];

  return (
    // Región con nombre: el listado de abajo se lee ENTERO contra esta franja,
    // así que un lector de pantalla tiene que poder volver a ella sin recorrer
    // las fichas. Y es lo que da un ancla estable a los tests, en vez de
    // caminar el DOM hacia arriba desde un texto.
    <section
      aria-labelledby="predio-tasado"
      className="border-border bg-muted/40 flex flex-wrap items-center gap-x-8 gap-y-3 rounded-xl border-[1.5px] px-4 py-3"
    >
      <div>
        <p
          id="predio-tasado"
          className="text-ink-hint text-[0.6875rem] font-extrabold tracking-[0.08em] uppercase"
        >
          Predio tasado
        </p>
        <p className="text-ink text-sm font-bold">
          {informe.propiedad_nombre || informe.numero_informe || "Sin nombre"}
        </p>
      </div>

      <Dato rotulo="Comuna" valor={comunas.join(" · ") || "—"} />
      <Dato
        rotulo="Superficie"
        valor={
          informe.superficie_ha
            ? `${informe.superficie_ha} ${informe.unidad_superficie ?? "ha"}`
            : "—"
        }
      />
      <Dato rotulo="Rol SII" valor={informe.roles?.[0]?.rol_sii ?? "—"} />

      <p className="text-ink-hint ml-auto flex max-w-xs items-start gap-1.5 text-xs">
        <Info className="mt-0.5 size-3.5 shrink-0" />
        Cada comparable se lee contra este predio: distancia, superficie y valor unitario.
      </p>
    </section>
  );
}

function Dato({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <p className="text-ink-hint text-[0.6875rem] font-extrabold tracking-[0.08em] uppercase">
        {rotulo}
      </p>
      <p className="text-ink-body font-mono text-sm tabular-nums">{valor}</p>
    </div>
  );
}

interface FiltrosProps {
  parametros: URLSearchParams;
  filtros: ReturnType<typeof leerFiltros>;
  catalogos: ReturnType<typeof useCatalogosDelPaso2>["data"];
  /**
   * ¿Todavía viajan las opciones?
   *
   * Va aparte de `catalogos` porque `undefined` no distingue "todavía no llegó"
   * de "llegó vacío", y los menús necesitan decir cosas distintas: uno es una
   * espera y el otro una respuesta.
   */
  catalogosCargando: boolean;
  clase: ClaseDeEvidencia;
  /** Cuántas fichas cumplen el filtro. `null` mientras viaja la consulta. */
  totalDelFiltro: number | null;
  onCambioDeLista: (clave: string, valores: string[]) => void;
  /**
   * Varias claves de la url en UNA escritura.
   *
   * No es una comodidad: dos llamadas seguidas parten las dos de la url del
   * render anterior, así que la segunda pisa a la primera y el extremo que se
   * escribió antes se pierde sin que nada falle.
   *
   * `null` BORRA la clave, y es lo que deja decir "sin tope" — que no es lo
   * mismo que un tope igual al extremo de la pista.
   */
  onCambioDeRango: (cambios: Record<string, number | string | null>) => void;
  /** La casilla "Incluir fichas con datos incompletos". */
  onCambioDeIncompletas: (incluir: boolean) => void;
  onLimpiar: () => void;
}

function Filtros({
  parametros,
  filtros,
  catalogos,
  catalogosCargando,
  clase,
  totalDelFiltro,
  onCambioDeLista,
  onCambioDeRango,
  onCambioDeIncompletas,
  onLimpiar,
}: FiltrosProps) {
  // Las de ESTA pestaña, no las de la url: ver `leerFiltros`.
  const comunas = filtros.comunas;

  /* Los bordes de la escala de años salen del CATÁLOGO —vienen del más nuevo al
     más viejo— y no del calendario: una escala que llegara hasta el año en curso
     ofrecería filtrar por años sin ninguna ficha.

     HACE FALTA UN AÑO DE DIFERENCIA, no uno solo. Con los dos bordes iguales el
     alto de la pista es cero y Radix reparte las posiciones dividiendo por él:
     la manija queda en `NaN` y el control se dibuja roto. Pasa en dos momentos
     reales —mientras los catálogos viajan, que es SIEMPRE al abrir, y con un
     catálogo de un solo año— así que el control se apaga y dice cuál de los
     dos es. */
  const anios = catalogos?.anios ?? [];
  const anioMasViejo = anios.length > 0 ? anios[anios.length - 1] : null;
  const anioMasNuevo = anios.length > 0 ? anios[0] : null;
  const hayEscalaDeAnios =
    anioMasViejo !== null && anioMasNuevo !== null && anioMasNuevo > anioMasViejo;

  return (
    /* LA GRILLA REEMPLAZÓ A UNA FILA QUE SE ENVOLVÍA SOLA.
       Con `flex-wrap`, cada control caía donde le tocaba según el ancho de la
       ventana: la fila de diecisiete casillas de años empujaba la distancia a
       un renglón propio, los rótulos quedaban a alturas distintas y no había
       dos pantallas que se vieran igual. Tres columnas fijas dan un lugar
       estable a cada filtro.

       `items-end` porque los controles no miden lo mismo —un combobox es más
       bajo que un slider con sus extremos— y alinearlos por arriba dejaba los
       rótulos escalonados. Apoyados abajo, los seis comparten línea de base. */
    <div className="border-border bg-superficie-sutil rounded-xl border-[1.5px] px-3.5 py-3">
      {/* LOS DIVISORES SOLO EXISTEN EN TRES COLUMNAS, y no es una simplificación
          visual: la línea es un `border-left` de una celda, así que qué celda
          la lleva depende de en qué columna cae — y eso cambia con el ancho. La
          celda 5 abre columna en la grilla de tres y va segunda en la de dos:
          la misma clase dibujaría la línea en el borde exterior de la tarjeta.
          Abajo de `lg` separa el espacio, que no puede quedar mal puesto. */}
      <div className="grid grid-cols-1 items-end gap-x-4 gap-y-3.5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-0">
        <div className="lg:px-4">
          <SelectorMultiple
            rotulo="Comuna"
            plural="comunas"
            opciones={(catalogos?.comunas ?? []).map((c) => ({
              valor: c.codigo,
              etiqueta: c.nombre,
              fichas: c.fichas,
            }))}
            seleccion={comunas}
            onCambio={(valores) => onCambioDeLista("comunas", valores)}
            cargando={catalogosCargando}
          />
        </div>

        <div className="border-divisor lg:border-l lg:px-4">
          <SelectorMultiple
            rotulo={clase === "transaccion" ? "Fuente del registro" : "Portal o corredor"}
            plural={clase === "transaccion" ? "fuentes" : "portales"}
            opciones={(catalogos?.fuentes ?? []).map((f) => ({
              valor: f.clave,
              etiqueta: f.clave,
              fichas: f.fichas,
            }))}
            cargando={catalogosCargando}
            seleccion={parametros.getAll("fuentes")}
            onCambio={(valores) => onCambioDeLista("fuentes", valores)}
          />
        </div>

        {/* LOS DOS FILTROS QUE FALTAN ACÁ —superficie y valor unitario— SE
            MUDARON A LOS EJES DEL GRÁFICO el 09-09-2026, que es lo que
            describen. Los cuatro que quedan no hablan de ningún eje: acotan
            el conjunto antes de dibujarlo. */}

        {/* EL AÑO ES UN RANGO desde el 08-09-2026, no diecisiete casillas.
            Elegir años salteados no es algo que se le pida a la evidencia de
            mercado —lo que se acota es una ventana reciente— y la fila de
            casillas se llevaba el ancho de toda la barra. */}
        <CampoDeRango
          rotulo="Año de la operación"
          valor={[
            filtros.anioDesde ?? anioMasViejo ?? 0,
            filtros.anioHasta ?? anioMasNuevo ?? 1,
          ]}
          onCambio={([desde, hasta]) =>
            // Los extremos del catálogo son "sin filtro" (ver `SIN_TOPE`): como
            // número, descartarían toda ficha sin fecha de publicación.
            onCambioDeRango({
              anio_desde: desde <= (anioMasViejo ?? desde) ? SIN_TOPE : desde,
              anio_hasta: hasta >= (anioMasNuevo ?? hasta) ? SIN_TOPE : hasta,
            })
          }
          min={anioMasViejo ?? 0}
          // Nunca igual al mínimo, ni siquiera apagado: con la pista de alto
          // cero, Radix divide por él y la manija sale en `NaN`.
          max={hayEscalaDeAnios ? (anioMasNuevo as number) : (anioMasViejo ?? 0) + 1}
          leerValor={([desde, hasta]) =>
            desde === hasta ? String(desde) : `${desde} – ${hasta}`
          }
          extremoMin={String(anioMasViejo ?? "—")}
          extremoMax={String(anioMasNuevo ?? "—")}
          deshabilitado={!hayEscalaDeAnios}
          motivo={
            catalogosCargando
              ? "Buscando los años del catálogo…"
              : "El catálogo tiene fichas de un solo año."
          }
          className="border-divisor lg:border-l lg:px-4"
        />

        {/* LA CLASE DE SUELO SOLO EXISTE EN TRANSACCIONES, y es la CUARTA
            celda de una grilla de tres: en `lg` abre fila propia en la
            columna 1 —mismo lugar que Comuna—, así que no lleva divisor
            (el borde de esta barra solo existe a la derecha de una celda que
            SIGUE a otra en la misma fila).

            Una oferta de portal no tiene rol, así que no llega a ningún
            predio y JAMÁS tiene clase de suelo: medido, 0 de 4.473 ofertas.
            Un selector permanentemente vacío en esa pestaña sería un control
            que no puede hacer nada, así que se OCULTA en vez de mostrarse
            apagado — al revés que "Año de la operación", que sí puede quedar
            sin escala y lo dice.

            Y NO SE SIEMBRA, al revés que comuna, año y superficie: la mayoría
            del catálogo no tiene clase (5.998 de 70.046 transacciones), así
            que sembrar cualquiera escondería fichas por una decisión que
            nadie tomó y el paso 2 abriría casi vacío. */}
        {clase === "transaccion" && (
          <div className="lg:px-4">
            <SelectorMultiple
              rotulo="Clase de suelo"
              plural="clases"
              opciones={(catalogos?.clases_de_suelo ?? []).map((c) => ({
                valor: c.codigo,
                // EL CÓDIGO ES LO QUE SE IMPRIME: "3R" es clase 3 con riego, y
                // no hay un nombre largo que mostrar en su lugar — mismo
                // criterio que la clave de fuentes, unas celdas más arriba.
                etiqueta: c.codigo,
                fichas: c.fichas,
              }))}
              cargando={catalogosCargando}
              seleccion={parametros.getAll("clases_de_suelo")}
              onCambio={(valores) => onCambioDeLista("clases_de_suelo", valores)}
            />
          </div>
        )}

        {/* EL FILTRO DE DISTANCIA SE MUDÓ DEBAJO DEL MAPA, que es lo que
            describe — la misma regla que llevó superficie y valor unitario a
            los ejes del gráfico. Acá quedan los que no hablan de ninguna de
            las dos lecturas. */}
      </div>

      {/* EL PIE DICE QUÉ PRODUJO EL FILTRO, y por eso está acá y no solo abajo
          de la tabla: el número que contesta "¿me pasé de estricto?" tiene que
          estar al lado de los controles que lo mueven. La cuenta de lo CARGADO
          sigue bajo "Cargar más", que es otra pregunta. */}
      <div className="border-divisor mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t pt-2.5">
        <p className="text-ink-hint font-mono text-xs tabular-nums">
          {totalDelFiltro === null
            ? "Buscando comparables…"
            : `${numero(totalDelFiltro)} ${totalDelFiltro === 1 ? "comparable" : "comparables"} en el filtro`}
        </p>

        {/* LA CASILLA VIVE AL LADO DEL CONTEO porque es lo que lo mueve: con
            ella puesta entran las fichas que no publican superficie, fecha o
            ubicación exacta, y el número de la izquierda sube. Va apagada por
            defecto —lo de siempre— y la decide quien tasa, nunca el sistema. */}
        <label className="flex cursor-pointer items-center gap-2">
          <Checkbox
            checked={filtros.incluirIncompletas}
            onCheckedChange={(valor) => onCambioDeIncompletas(valor === true)}
          />
          <span className="text-ink-body text-caption font-semibold">
            Incluir fichas con datos incompletos
          </span>
        </label>

        <Button variant="ghost" size="sm" onClick={onLimpiar}>
          Limpiar filtros
        </Button>
      </div>

      {/* EL AVISO DE LO QUE LA CASILLA NO LEVANTA. Con un filtro de distancia
          puesto, las fichas ubicadas en el centro de su comuna siguen fuera: su
          ubicación es la de la comuna y no la de la propiedad, así que la
          distancia que mostrarían sería falsa. Sin este aviso quien la enciende
          cree que ya lo ve todo y el conteo no le cuadra. */}
      {filtros.incluirIncompletas && filtros.distanciaMaxKm !== undefined && (
        <p className="text-ink-hint mt-2 flex items-start gap-1.5 text-xs">
          <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          Con el filtro de distancia puesto, los comparables de ubicación aproximada (el centro
          de su comuna) siguen fuera: su distancia al predio no sería real.
        </p>
      )}
    </div>
  );
}

/**
 * De a cuánto avanza el control de valor unitario.
 *
 * NO es 1. La escala la fija el mercado del filtro y va de unos cientos de
 * UF/ha a varios cientos de miles, así que un paso fijo o sobra o no llega: con
 * paso 1 sobre una escala de 350.000, mover la manija con el teclado es
 * inviable, y el arrastre queda con una precisión que nadie necesita.
 *
 * Se apunta a ~120 posiciones y se redondea a la potencia de diez de abajo,
 * para que los valores que el control produce sean números que alguien pueda
 * leer y repetir.
 */
function pasoDeLaEscala(techo: number): number {
  if (techo <= 0) return 1;

  const bruto = techo / 120;

  return Math.max(10 ** Math.floor(Math.log10(bruto)), 1);
}

function leerClase(valor: string | null): ClaseDeEvidencia {
  return valor === "oferta" ? "oferta" : "transaccion";
}

const ORDENES: OrdenDelCatalogo[] = [
  "distancia",
  "valor_unitario",
  "mas_recientes",
  "superficie",
];

/**
 * Los filtros leídos de la url.
 *
 * `distancia_max` se DESCARTA si el informe no tiene coordenada o si no hay
 * comuna: sin eso, un enlace guardado con ese parámetro haría que la pantalla
 * pidiera algo que el servidor rechaza con un 422 y el usuario vería un error
 * sin haber tocado nada.
 *
 * Y SE DESCARTA TAMBIÉN EN EL TOPE. La pantalla ya no lo escribe, pero los
 * enlaces guardados con el defecto anterior existen, y hay que leerlos como lo
 * que la pantalla dice de ellos: el control muestra "sin tope" en el extremo, y
 * un valor POR ENCIMA de la escala Radix lo recorta a ese mismo extremo, así
 * que cualquiera de los dos filtraría por un radio que el control niega estar
 * aplicando.
 */
function leerFiltros(
  parametros: URLSearchParams,
  tieneCoordenada: boolean,
  comunasDelCatalogo?: { codigo: string }[],
) {
  // SOLO CUENTAN LAS COMUNAS QUE ESTA PESTAÑA TIENE. El CBR cubre cuatro y la
  // siembra pone la del predio, que casi nunca está entre ellas: el selector
  // decía "2 elegidas" sin ninguna casilla marcada y la consulta se iba
  // filtrando por comunas sin una sola ficha. Lo verdadero ahí es que no hay
  // ninguna elegida, así que la pantalla y el servidor leen lo mismo.
  //
  // LA URL NO SE TOCA: en Ofertas esa comuna sí existe, y al volver a esa
  // pestaña tiene que seguir puesta. Y mientras el catálogo viaja se deja la
  // lista entera, o saldría una consulta por el país completo sin que nadie
  // haya quitado nada.
  const enLaUrl = parametros.getAll("comunas");
  const disponibles = comunasDelCatalogo
    ? new Set(comunasDelCatalogo.map((c) => c.codigo))
    : null;
  const comunas = disponibles ? enLaUrl.filter((c) => disponibles.has(c)) : enLaUrl;
  const ordenPedido = parametros.get("orden") as OrdenDelCatalogo | null;
  const orden =
    ordenPedido && ORDENES.includes(ordenPedido)
      ? ordenPedido === "distancia" && !tieneCoordenada
        ? undefined
        : ordenPedido
      : undefined;

  const distancia = leerNumero(parametros.get("distancia_max"));
  const distanciaMaxKm =
    distancia !== undefined && distancia < DISTANCIA_MAX ? distancia : undefined;

  return {
    comunas,
    fuentes: parametros.getAll("fuentes"),
    // LA CLASE DE SUELO SOLO SE LEE EN TRANSACCIONES, y el guardián va acá y no
    // solo en `cambiarLaClase`: soltarla al cambiar de pestaña tapa el camino
    // que pasa por la pantalla y deja abierto el que entra de afuera. Quien
    // abre una url que YA trae las dos cosas —un enlace compartido, un
    // marcador, el historial— nunca pasa por aquel, así que la consulta se iba
    // con el filtro, Ofertas volvía sin una sola ficha y el selector que lo
    // sacaría está escondido en esa pestaña. La lectura es el único punto por
    // el que pasan todas las entradas.
    //
    // `leerClase` y no comparar el texto crudo: es la misma función con la que
    // la pantalla decide qué pestaña está activa, así que el guardián y el
    // control que se dibuja no pueden discrepar.
    //
    // Es el trato que `distanciaMaxKm` ya recibe tres líneas más abajo.
    clasesDeSuelo:
      leerClase(parametros.get("clase")) === "transaccion"
        ? parametros.getAll("clases_de_suelo")
        : [],
    anioDesde: leerNumero(parametros.get("anio_desde")),
    anioHasta: leerNumero(parametros.get("anio_hasta")),
    superficieMinHa: leerNumero(parametros.get("superficie_min")),
    superficieMaxHa: leerNumero(parametros.get("superficie_max")),
    valorMinUfHa: leerNumero(parametros.get("valor_min_uf_ha")),
    valorMaxUfHa: leerNumero(parametros.get("valor_max_uf_ha")),
    distanciaMaxKm: tieneCoordenada && comunas.length > 0 ? distanciaMaxKm : undefined,
    incluirIncompletas: leerIncluirIncompletas(parametros),
    orden,
  };
}

/**
 * "Incluir fichas con datos incompletos": solo el `1` la enciende. Lo demás —
 * ausente, vacío, cualquier otro texto— es apagada, que es lo de siempre; un
 * enlace con un valor raro no debería abrir un conjunto distinto del que la
 * pantalla dice mostrar.
 */
function leerIncluirIncompletas(parametros: URLSearchParams): boolean {
  return parametros.get("incluir_incompletas") === "1";
}

function leerNumero(valor: string | null): number | undefined {
  if (valor === null || valor === "") return undefined;
  const numero = Number(valor);

  return Number.isFinite(numero) ? numero : undefined;
}

